import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../constants/config';
import { SESSION_KEY } from '../constants/storageKeys';

class AuthError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function authRequest(path, options = {}) {
  const sessionToken = localStorage.getItem(SESSION_KEY);
  const headers = { 'Content-Type': 'application/json' };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const res = await fetch(`${API_URL}${path}`, { headers, ...options });
  if (!res.ok) {
    let msg = 'Request failed';
    try { const data = await res.json(); msg = data.error || msg; } catch {}
    throw new AuthError(msg, res.status);
  }
  return await res.json();
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Check session on mount
  useEffect(() => {
    const sessionToken = localStorage.getItem(SESSION_KEY);
    if (!sessionToken) {
      setLoading(false);
      return;
    }

    authRequest('/api/auth/me')
      .then((data) => setUser(data.user))
      .catch((err) => {
        // Only clear session on auth errors (401), not network failures
        if (err.status === 401) {
          localStorage.removeItem(SESSION_KEY);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const sendCode = useCallback(async (email) => {
    await authRequest('/api/auth/send-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }, []);

  const verify = useCallback(async (email, code, displayName, subscribed) => {
    const data = await authRequest('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code, displayName, subscribed: !!subscribed }),
    });
    localStorage.setItem(SESSION_KEY, data.sessionToken);
    setUser(data.user);
    setShowAuthModal(false);
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore errors
    }
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const data = await authRequest('/api/auth/profile', {
      method: 'POST',
      body: JSON.stringify(updates),
    });
    setUser(data.user);
    return data.user;
  }, []);

  const deleteAccount = useCallback(async () => {
    await authRequest('/api/auth/account', { method: 'DELETE' });
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authRequest('/api/auth/me');
      setUser(data.user);
    } catch {
      // Ignore
    }
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    showAuthModal,
    setShowAuthModal,
    sendCode,
    verify,
    signOut,
    updateProfile,
    deleteAccount,
    refreshUser,
  };
}
